import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useSetAtom } from 'jotai';
import {
  ProvisionUploadForm,
  type ProvisionPayload,
  type ProvisionUploadFormHandle,
} from 'components/add-document-modal/provision-upload-form';
import {
  provisionRelatedItemAtom,
  relatedItemProvisionUrl,
  type RelatedItemsHost,
} from './atoms';
import {
  PROVISIONABLE_KIND_DOCUMENT_TYPES,
  type RelatedItemKind,
} from './types';

// The panel owns the single Save button, so it drives submit through this handle.
export interface ProvisionUploadHandle {
  submit: () => void;
}

interface Props {
  host: RelatedItemsHost;
  kind: RelatedItemKind;
  /** Report whether Save may fire (file uploaded + config chosen). */
  onReadyChange: (ready: boolean) => void;
  /** Mirror the in-flight state onto the panel's Save button. */
  onPendingChange: (pending: boolean) => void;
  /** Document provisioned + linked — close and refresh. */
  onSaved: () => void;
}

// Upload + provision a new document for a record kind, and link it to the host.
//
// The form itself — upload atom, OCR/LLM/prompt selection, config validation — lives in
// add-document-modal/provision-upload-form, shared with the Vesting Document panel. What stays
// here is the part that is actually about Related Items: the endpoint, the `related_item`
// payload wrapper, and the kind → document_type mapping.
export const ProvisionUploadSection = forwardRef<ProvisionUploadHandle, Props>(
  ({ host, kind, onReadyChange, onPendingChange, onSaved }, ref) => {
    const provision = useSetAtom(provisionRelatedItemAtom);
    const formRef = useRef<ProvisionUploadFormHandle>(null);

    const [submitting, setSubmitting] = useState(false);
    // Synchronous guard: `submitting` state lags a render, so a fast double-click could fire
    // two POSTs (two provisioned documents) before it flips.
    const submittingRef = useRef(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = (payload: ProvisionPayload) => {
      if (submittingRef.current) return;
      submittingRef.current = true;
      setSubmitting(true);
      onPendingChange(true);
      setError(null);
      provision({
        url: relatedItemProvisionUrl(host),
        method: 'POST',
        data: { related_item: { kind, provision: payload } },
        onSuccess: () => {
          submittingRef.current = false;
          setSubmitting(false);
          onPendingChange(false);
          onSaved();
        },
        onError: (message?: string) => {
          submittingRef.current = false;
          setSubmitting(false);
          onPendingChange(false);
          setError(message ?? 'Failed to upload and provision the document.');
        },
      });
    };

    useImperativeHandle(ref, () => ({
      submit: () => formRef.current?.submit(),
    }));

    return (
      <ProvisionUploadForm
        ref={formRef}
        documentType={PROVISIONABLE_KIND_DOCUMENT_TYPES[kind]}
        idPrefix={`provision-${kind}-`}
        onReadyChange={onReadyChange}
        onSubmit={handleSubmit}
        submitting={submitting}
        error={error}
        onDismissError={() => setError(null)}
      />
    );
  }
);

ProvisionUploadSection.displayName = 'ProvisionUploadSection';
